"use client"

import {
  type GachaEvmChain,
  gachaEvmAddressSchema,
  getGachaPreferredToken,
} from "@anaconda/core/gacha/evm-chains"
import { Trans, useLingui } from "@lingui/react/macro"
import { Dialog, DialogTrigger } from "@rarible_int/anaconda_ui_components"
import { atom, wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { http, createPublicClient, erc20Abi, formatUnits } from "viem"
import {
  GachaBalanceDialogView,
  GachaPaymentTokenIcon,
} from "./gacha-balance-dialog"

export const GACHA_EVM_BALANCE_REFRESH_EVENT = "gacha:evm-balance-refresh"
export function requestGachaEvmBalanceRefresh() {
  window.dispatchEvent(new Event(GACHA_EVM_BALANCE_REFRESH_EVENT))
}

function createGachaBalanceDialogState() {
  return atom(false, "gacha-evm-payment-balance.open")
}

export const GachaEvmPaymentBalance = reatomComponent<{
  chain: GachaEvmChain
  walletAddress?: string
  onDeposit?: () => Promise<void>
}>(({ chain, walletAddress, onDeposit }) => {
  const { t } = useLingui()
  const openState = useMemo(createGachaBalanceDialogState, [])
  const openDialog = openState()
  const setOpenDialog = openState.set
  const queryClient = useQueryClient()
  const token = getGachaPreferredToken(chain)
  const identity = `${chain.chainId}:${chain.rpcUrl}:${token.address}:${token.decimals}:${walletAddress}`
  const queryKey = ["gacha-payment-balance", identity]
  const query = useQuery({
    queryKey,
    enabled: Boolean(walletAddress),
    retry: false,
    refetchOnWindowFocus: true,
    queryFn: wrap(async ({ signal }: { signal: AbortSignal }) => {
      const address = gachaEvmAddressSchema.parse(walletAddress)
      const client = createPublicClient({
        transport: http(chain.rpcUrl, {
          fetchOptions: { signal },
          retryCount: 0,
        }),
      })
      const chainId = await wrap(client.getChainId())
      if (chainId !== chain.chainId)
        throw new Error("Payment balance RPC chain mismatch")
      const [decimals, raw] = await wrap(
        Promise.all([
          client.readContract({
            address: token.address,
            abi: erc20Abi,
            functionName: "decimals",
          }),
          client.readContract({
            address: token.address,
            abi: erc20Abi,
            functionName: "balanceOf",
            args: [address],
          }),
        ]),
      )
      if (decimals !== token.decimals)
        throw new Error("Payment token decimals mismatch")
      return formatUnits(raw, token.decimals)
    }),
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: Reatom wrap hides the callback; the complete identity owns this query and refresh listener.
  useEffect(
    wrap(() => {
      const refresh = wrap(() => {
        void wrap(queryClient.invalidateQueries({ queryKey }))
      })
      window.addEventListener(GACHA_EVM_BALANCE_REFRESH_EVENT, refresh)
      return () =>
        window.removeEventListener(GACHA_EVM_BALANCE_REFRESH_EVENT, refresh)
    }),
    [queryClient, identity],
  )
  const isLoading =
    Boolean(walletAddress) && (query.isPending || query.isPlaceholderData)
  const balance =
    query.isError || query.isPlaceholderData || query.data === undefined
      ? "—"
      : new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(query.data))
  return (
    <Dialog open={openDialog} onOpenChange={setOpenDialog}>
      <DialogTrigger asChild>
        <button
          type="button"
          disabled={!walletAddress}
          aria-label={t`Payment balance`}
          aria-busy={isLoading}
          title={`${chain.name}: ${balance} ${token.symbol}`}
          className="inline-flex min-w-0 max-w-[130px] items-center gap-1.5 whitespace-nowrap px-0 align-middle font-headline text-xs text-foreground transition-transform hover:scale-100 active:scale-95 disabled:cursor-not-allowed md:max-w-[200px] md:gap-2 md:text-sm"
        >
          <GachaPaymentTokenIcon
            symbol={token.symbol}
            icon={token.icon}
            className="h-3.5 w-3.5 md:h-4 md:w-4"
          />
          {isLoading ? (
            <span className="h-3.5 w-12 animate-pulse rounded bg-foreground/10 md:h-4 md:w-14" />
          ) : (
            <>
              <span className="min-w-0 truncate">{balance}</span>{" "}
              <span className="shrink-0">{token.symbol}</span>
            </>
          )}
          <span className="sr-only">
            {!walletAddress && <Trans>Connect a wallet to view balance</Trans>}
          </span>
        </button>
      </DialogTrigger>
      {walletAddress && (
        <GachaBalanceDialogView
          balanceAmount={balance}
          walletAddress={walletAddress}
          tokenSymbol={token.symbol}
          tokenIcon={token.icon}
          networkName={chain.name}
          onDeposit={
            onDeposit
              ? () => {
                  setOpenDialog(false)
                  void onDeposit()
                }
              : undefined
          }
        />
      )}
    </Dialog>
  )
}, "GachaEvmPaymentBalance")
